import { type DatabaseSdkContract } from '@databases/sdk-types';
import { getService } from '@databases/service-container';
import { type DatabaseField } from '@databases/types/database';
import {
  DatabaseCoverFieldType,
  type DatabaseView,
  shouldShowField
} from '@databases/types/database/view';
import {
  type ViewChangeType,
  ViewChangeTypes
} from '@databases/utils/database/view/view';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  getTimelineBarColorOverridePropertyKey,
  getTimelineBarColorOverrides
} from './timeline-colors';
import {
  getDefaultTimelineFields,
  isTimelineDateField,
  type TimelineDateField
} from './timeline-utils';
import {
  DEFAULT_TIMELINE_LAYOUT_SETTINGS,
  TIMELINE_LAYOUT_ID,
  timelineLayoutSchema,
  type TimelineGrouping,
  type TimelineBarColor,
  type TimelineLayoutSettings,
  type TimelineScale
} from './view-layout-module';

export type TimelineSettings = {
  endDateFieldId?: string;
  fixedColumnWidth?: number;
  grouping?: TimelineGrouping;
  hideEmptyGroups?: boolean;
  showJumpToBarButtons?: boolean;
  showNumberedEntries?: boolean;
  startDateFieldId?: string;
  timelineScale?: TimelineScale;
};

type TimelineLayoutPropertiesMap = {
  [TIMELINE_LAYOUT_ID]: TimelineLayoutSettings;
};

type TimelineDatabaseSdk = DatabaseSdkContract<TimelineLayoutPropertiesMap>;

type TimelineBarColorOverridesPatch =
  | { type: 'set'; values: Record<string, TimelineBarColor> }
  | { type: 'delete'; keys: string[] }
  | { type: 'clear' };

const getTimelineDatabaseSdk = (): TimelineDatabaseSdk =>
  getService('DatabaseSdk') as TimelineDatabaseSdk;

const isNoDateFieldSetting = (fieldId: string | undefined) =>
  fieldId === DatabaseCoverFieldType.NONE;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

export const useTimelineSettings = ({
  databaseSdk,
  enabled = true,
  fields,
  liveEntryIds,
  view,
  setViewChange,
  preferViewDraftSettings = false
}: {
  databaseSdk?: TimelineDatabaseSdk;
  enabled?: boolean;
  fields: DatabaseField[];
  liveEntryIds?: readonly string[];
  view: DatabaseView;
  setViewChange?: (delta: ViewChangeType | ViewChangeType[]) => void;
  preferViewDraftSettings?: boolean;
}) => {
  const resolvedDatabaseSdk = useMemo(
    () => (enabled ? (databaseSdk ?? getTimelineDatabaseSdk()) : undefined),
    [databaseSdk, enabled]
  );
  const views = useMemo(() => resolvedDatabaseSdk?.views, [resolvedDatabaseSdk]);
  const persistedBarColorOverrides = useMemo(
    () => (enabled ? getTimelineBarColorOverrides(view.settings.properties) : {}),
    [enabled, view.settings.properties]
  );
  const persistedSettings = useMemo(
    () =>
      enabled && views ? views.getProperties(view.id, TIMELINE_LAYOUT_ID) : undefined,
    // The projected properties reference is the revision signal for the SDK read.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [enabled, view.id, view.settings.properties, views]
  );
  const stagedSettings = useMemo(() => {
    if (!setViewChange && !preferViewDraftSettings) {
      return undefined;
    }

    const properties = view.settings.properties?.[TIMELINE_LAYOUT_ID];
    const parsedSettings = timelineLayoutSchema.safeParse(properties);

    return parsedSettings.success ? parsedSettings.data : undefined;
  }, [preferViewDraftSettings, setViewChange, view.settings.properties]);
  const timelineProperties = stagedSettings ?? persistedSettings;
  const timelineGrouping =
    timelineProperties?.grouping ?? DEFAULT_TIMELINE_LAYOUT_SETTINGS.grouping;
  const timelineGroupingKey = timelineGrouping.groupBy
    ? JSON.stringify([
        timelineGrouping.groupBy.fieldId,
        timelineGrouping.groupBy.sortType,
        timelineGrouping.groupBy.manualGroupOrder
      ])
    : '';
  const stableTimelineGroupingRef = useRef({
    key: timelineGroupingKey,
    value: timelineGrouping
  });
  if (stableTimelineGroupingRef.current.key !== timelineGroupingKey) {
    stableTimelineGroupingRef.current = {
      key: timelineGroupingKey,
      value: timelineGrouping
    };
  }
  const stableTimelineGrouping = stableTimelineGroupingRef.current.value;
  const persistedDateFieldSettings = useMemo(
    () => ({
      hasNoEndDateField: isNoDateFieldSetting(timelineProperties?.endDateFieldId),
      hasNoStartDateField: isNoDateFieldSetting(timelineProperties?.startDateFieldId)
    }),
    [timelineProperties?.endDateFieldId, timelineProperties?.startDateFieldId]
  );
  const persistedTimelineSettings = useMemo<TimelineLayoutSettings>(
    () => ({
      endDateFieldId:
        timelineProperties?.endDateFieldId ??
        DEFAULT_TIMELINE_LAYOUT_SETTINGS.endDateFieldId,
      startDateFieldId:
        timelineProperties?.startDateFieldId ??
        DEFAULT_TIMELINE_LAYOUT_SETTINGS.startDateFieldId,
      hideEmptyGroups:
        timelineProperties?.hideEmptyGroups ??
        DEFAULT_TIMELINE_LAYOUT_SETTINGS.hideEmptyGroups,
      showJumpToBarButtons:
        timelineProperties?.showJumpToBarButtons ??
        DEFAULT_TIMELINE_LAYOUT_SETTINGS.showJumpToBarButtons,
      showNumberedEntries:
        timelineProperties?.showNumberedEntries ??
        DEFAULT_TIMELINE_LAYOUT_SETTINGS.showNumberedEntries,
      timelineFixedColumnWidth:
        timelineProperties?.timelineFixedColumnWidth ??
        DEFAULT_TIMELINE_LAYOUT_SETTINGS.timelineFixedColumnWidth,
      timelineScale:
        timelineProperties?.timelineScale ??
        DEFAULT_TIMELINE_LAYOUT_SETTINGS.timelineScale,
      grouping: stableTimelineGrouping
    }),
    [
      timelineProperties?.endDateFieldId,
      timelineProperties?.hideEmptyGroups,
      timelineProperties?.showJumpToBarButtons,
      timelineProperties?.startDateFieldId,
      timelineProperties?.showNumberedEntries,
      timelineProperties?.timelineFixedColumnWidth,
      timelineProperties?.timelineScale,
      stableTimelineGrouping
    ]
  );
  const [storedSettings, setStoredSettings] = useState<TimelineLayoutSettings>(
    persistedTimelineSettings
  );
  const [storedBarColorOverrides, setStoredBarColorOverrides] = useState(
    persistedBarColorOverrides
  );
  const [storedDateFieldSettings, setStoredDateFieldSettings] = useState(
    persistedDateFieldSettings
  );

  useEffect(() => {
    setStoredSettings(persistedTimelineSettings);
    setStoredBarColorOverrides(persistedBarColorOverrides);
    setStoredDateFieldSettings(persistedDateFieldSettings);
  }, [
    persistedBarColorOverrides,
    persistedDateFieldSettings,
    persistedTimelineSettings,
    view.id
  ]);

  const dateFields = useMemo(
    () =>
      fields.filter((field): field is TimelineDateField => isTimelineDateField(field)),
    [fields]
  );

  const defaultFields = useMemo(() => getDefaultTimelineFields(fields), [fields]);

  const fixedField = useMemo(
    () => fields.find((field) => shouldShowField(view.settings.fields, field.id)),
    [fields, view.settings.fields]
  );
  const storedStartDateField = dateFields.find(
    (field) => field.id === storedSettings.startDateFieldId
  );
  const storedEndDateField = dateFields.find(
    (field) => field.id === storedSettings.endDateFieldId
  );
  const startDateField =
    storedStartDateField ??
    (storedDateFieldSettings.hasNoStartDateField
      ? undefined
      : defaultFields.startDateField?.id !== storedEndDateField?.id
        ? defaultFields.startDateField
        : dateFields.find((field) => field.id !== storedEndDateField?.id));
  const endDateField =
    storedEndDateField ??
    (storedDateFieldSettings.hasNoEndDateField
      ? undefined
      : defaultFields.endDateField?.id !== startDateField?.id
        ? defaultFields.endDateField
        : dateFields.find((field) => field.id !== startDateField?.id));
  const fixedColumnWidth = storedSettings.timelineFixedColumnWidth;
  const timelineScale = storedSettings.timelineScale;

  const updateSettings = useCallback(
    (nextSettings: Partial<TimelineSettings>) => {
      if (!enabled || (!setViewChange && !views)) {
        return;
      }

      const { fixedColumnWidth, grouping, ...settingsWithoutSpecialKeys } = nextSettings;
      const hasFixedColumnWidth = Object.prototype.hasOwnProperty.call(
        nextSettings,
        'fixedColumnWidth'
      );
      const hasGrouping = grouping !== undefined;

      if (
        Object.keys(settingsWithoutSpecialKeys).length > 0 ||
        hasFixedColumnWidth ||
        hasGrouping
      ) {
        const nextStoredSettings = {
          ...storedSettings,
          ...settingsWithoutSpecialKeys,
          ...(hasFixedColumnWidth
            ? {
                timelineFixedColumnWidth:
                  fixedColumnWidth ??
                  DEFAULT_TIMELINE_LAYOUT_SETTINGS.timelineFixedColumnWidth
              }
            : undefined),
          ...(hasGrouping ? { grouping } : undefined)
        };

        const set = {
          ...settingsWithoutSpecialKeys,
          ...(hasFixedColumnWidth && fixedColumnWidth !== undefined
            ? { timelineFixedColumnWidth: fixedColumnWidth }
            : undefined),
          ...(hasGrouping ? { grouping } : undefined)
        };
        const unset =
          hasFixedColumnWidth && fixedColumnWidth === undefined
            ? ['timelineFixedColumnWidth' as const]
            : undefined;

        if (setViewChange) {
          const existingProperties = view.settings.properties ?? {};
          const existingLayoutProperties = existingProperties[TIMELINE_LAYOUT_ID];
          const existingLayoutPropertiesRecord = isRecord(existingLayoutProperties)
            ? existingLayoutProperties
            : (persistedSettings ?? {});
          const nextTimelineProperties = { ...existingLayoutPropertiesRecord, ...set };

          for (const key of unset ?? []) {
            delete nextTimelineProperties[key];
          }

          const nextProperties = { ...existingProperties };
          if (Object.keys(nextTimelineProperties).length > 0) {
            nextProperties[TIMELINE_LAYOUT_ID] = nextTimelineProperties;
          } else {
            delete nextProperties[TIMELINE_LAYOUT_ID];
          }

          setViewChange({
            type: ViewChangeTypes.VIEW_PROPERTIES,
            value: nextProperties
          });
          // setViewChange stages the change in the local draft; persistence happens
          // when the user saves, so keep hook state aligned with that staged view.
          setStoredSettings(nextStoredSettings);
          setStoredDateFieldSettings({
            hasNoEndDateField: isNoDateFieldSetting(nextStoredSettings.endDateFieldId),
            hasNoStartDateField: isNoDateFieldSetting(nextStoredSettings.startDateFieldId)
          });
          return;
        }

        if (!views) {
          return;
        }

        const result = views.updateProperties({
          viewId: view.id,
          layoutType: TIMELINE_LAYOUT_ID,
          ...(Object.keys(set).length > 0 ? { set } : undefined),
          ...(unset ? { unset } : undefined)
        });

        if (result.isOk()) {
          setStoredSettings(nextStoredSettings);
          setStoredDateFieldSettings({
            hasNoEndDateField:
              settingsWithoutSpecialKeys.endDateFieldId !== undefined
                ? isNoDateFieldSetting(settingsWithoutSpecialKeys.endDateFieldId)
                : storedDateFieldSettings.hasNoEndDateField,
            hasNoStartDateField:
              settingsWithoutSpecialKeys.startDateFieldId !== undefined
                ? isNoDateFieldSetting(settingsWithoutSpecialKeys.startDateFieldId)
                : storedDateFieldSettings.hasNoStartDateField
          });
        }
      }
    },
    [
      enabled,
      persistedSettings,
      setViewChange,
      storedDateFieldSettings,
      storedSettings,
      view.id,
      view.settings.properties,
      views
    ]
  );

  const updateBarColorOverrides = useCallback(
    (patch: TimelineBarColorOverridesPatch) => {
      if (!enabled || (!setViewChange && !views)) {
        return;
      }

      const nextOverrides = { ...storedBarColorOverrides };
      const set: Record<string, TimelineBarColor> = {};
      const unset: string[] = [];

      if (patch.type === 'set') {
        Object.entries(patch.values).forEach(([entryId, color]) => {
          nextOverrides[entryId] = color;
          set[getTimelineBarColorOverridePropertyKey(entryId)] = color;
        });
      } else {
        const entryIds =
          patch.type === 'delete' ? patch.keys : Object.keys(nextOverrides);
        entryIds.forEach((entryId) => {
          delete nextOverrides[entryId];
          unset.push(getTimelineBarColorOverridePropertyKey(entryId));
        });
      }

      if (Object.keys(set).length === 0 && unset.length === 0) {
        return;
      }

      if (setViewChange) {
        const nextProperties = { ...(view.settings.properties ?? {}), ...set };
        unset.forEach((key) => delete nextProperties[key]);
        setViewChange({
          type: ViewChangeTypes.VIEW_PROPERTIES,
          value: nextProperties
        });
        setStoredBarColorOverrides(nextOverrides);

        return;
      }

      const result = views?.updateProperties({
        viewId: view.id,
        ...(Object.keys(set).length > 0 ? { set } : undefined),
        ...(unset.length > 0 ? { unset } : undefined)
      });

      if (result?.isOk()) {
        setStoredBarColorOverrides(nextOverrides);
      }
    },
    [
      enabled,
      setViewChange,
      storedBarColorOverrides,
      view.id,
      view.settings.properties,
      views
    ]
  );

  useEffect(() => {
    if (!liveEntryIds) {
      return;
    }

    const liveEntryIdsSet = new Set(liveEntryIds);
    const colorOverrideEntries = Object.entries(storedBarColorOverrides);
    const deletedEntryIds = colorOverrideEntries
      .filter(([entryId]) => !liveEntryIdsSet.has(entryId))
      .map(([entryId]) => entryId);

    if (deletedEntryIds.length > 0) {
      updateBarColorOverrides({ type: 'delete', keys: deletedEntryIds });
    }
  }, [liveEntryIds, storedBarColorOverrides, updateBarColorOverrides]);

  return {
    barColorOverrides: storedBarColorOverrides,
    dateFields,
    endDateField,
    fixedColumnWidth,
    fixedField,
    grouping: storedSettings.grouping,
    hideEmptyGroups: storedSettings.hideEmptyGroups,
    showJumpToBarButtons: storedSettings.showJumpToBarButtons,
    showNumberedEntries: storedSettings.showNumberedEntries,
    startDateField,
    timelineScale,
    updateBarColorOverrides,
    updateSettings
  };
};
